import Immutable from 'immutable';
import { createSelector } from 'reselect';
import { selectBibEntities } from 'app/selectors/EntitiesSelector';
import { selectCurrentLibrary, selectLibraryLinkedData } from 'app/selectors/LibrarySelector';
import { selectCatalogBib } from 'app/selectors/catalogBibs/CatalogBibSelector';

import { CREATIVE_TYPES } from 'app/constants/BibConstants';

const BOOK_FORMAT_MAP = {
  AUDIOBOOK: 'http://schema.org/AudiobookFormat',
  EBOOK: 'http://schema.org/EBook',
  PAPERBACK: 'http://schema.org/Paperback',
  BK: 'http://schema.org/Hardcover'
};

function getCreativeType(bib) {
  const superFormats = bib.getIn(['briefInfo', 'superFormats']);
  if (superFormats.includes('BOOKS')) return CREATIVE_TYPES.BOOK;
  if (superFormats.includes('MUSIC_SOUND')) return CREATIVE_TYPES.MUSIC_ALBUM;
  if (superFormats.includes('MOVIES_TV_VIDEO')) return CREATIVE_TYPES.MOVIE;
  if (superFormats.includes('AUDIOBOOKS_SPOKEN_WORD')) return CREATIVE_TYPES.AUDIOBOOK;
  return CREATIVE_TYPES.CREATIVE_WORK;
}

function getAdditionalCreativeProperties(bib, catalogBib) {
  switch (getCreativeType(bib)) {
    case CREATIVE_TYPES.MOVIE:
    case CREATIVE_TYPES.MUSIC_ALBUM: {
      return {
        // TODO: Add contributor and director
        copyrightYear: catalogBib.getIn(['brief', 'publicationDate'])
      };
    }

    case CREATIVE_TYPES.BOOK:
    case CREATIVE_TYPES.AUDIOBOOK: {
      return {
        isbn: bib.getIn(['briefInfo', 'isbns']),
        bookEdition: bib.getIn(['briefInfo', 'edition']),
        bookFormat: {
          '@id': BOOK_FORMAT_MAP[bib.getIn(['briefInfo', 'format'])] || BOOK_FORMAT_MAP.BK,
          '@type': 'BookFormatType'
        }
      };
    }

    default:
      return {};
  }
}

function getAvailabilityStatus(availability) {
  switch (availability.get('statusType')) {
    case 'AVAILABLE':
    case 'ON_ORDER': {
      return 'http://schema.org/InStock';
    }

    default:
      return 'http://schema.org/OutOfStock';
  }
}

function getAvailabilityLocation(library) {
  const geoLocation = library.get('linkedDataGeoLocation');

  if (geoLocation && geoLocation.get('address')) {
    return Immutable.fromJS({
      '@type': 'Place',
      address: geoLocation.get('address'),
      hasMap: geoLocation.get('hasMap'),
      geo: {
        '@type': 'GeoCoordinates',
        latitude: geoLocation.get('latitude'),
        longitude: geoLocation.get('longitude')
      }
    });
  }

  return undefined;
}

function getOffers(bib, library, libraryLinkedData) {
  return Immutable.Map({
    '@type': 'Offer',
    availability: getAvailabilityStatus(bib.get('availability', Immutable.Map())),
    availableAtOrFrom: getAvailabilityLocation(library),
    sku: bib.getIn(['briefInfo', 'callNumber']),
    seller: libraryLinkedData,
    price: '0.00'
  });
}

export const selectLinkedDataForCurrentBib = createSelector(
  [selectCurrentLibrary, selectLibraryLinkedData, selectBibEntities, selectCatalogBib],
  (library, libraryLinkedData, bibs, catalogBib) => {
    if (!catalogBib) {
      return Immutable.Map();
    }

    const bib = bibs.get(catalogBib.get('metadataId'));
    const bibBrief = bib.get('briefInfo', Immutable.Map());
    const catalogBrief = catalogBib.get('brief', Immutable.Map());
    const title = catalogBrief.get('otherScriptTitle') || catalogBrief.get('title');
    const description = catalogBrief.get('description');
    const authors = catalogBrief.get('creators', Immutable.List());
    const aggregateRating = bibBrief.get('rating', Immutable.Map());

    const creativeWorkSchema = Immutable.Map({
      '@context': 'http://schema.org',
      '@type': getCreativeType(bib),
      name: title,
      about: bibBrief.get('subjectHeadings'),
      aggregateRating: aggregateRating.get('totalCount')
        ? {
            '@type': 'AggregateRating',
            bestRating: 10,
            worstRating: 1,
            ratingCount: aggregateRating.get('totalCount'),
            ratingValue: aggregateRating.get('averageRating') / 10
          }
        : undefined,
      author:
        authors.size > 0
          ? authors.map(author => ({
              '@type': 'Person',
              name: author.get('fullName')
            }))
          : undefined,
      datePublished: catalogBrief.get('publicationDate'),
      description: description ? [description] : undefined,
      image: catalogBrief.getIn(['coverImage', 'large']),
      ...getAdditionalCreativeProperties(bib, catalogBib)
    });

    const productSchema = Immutable.Map({
      '@context': 'http://schema.org',
      '@type': 'Product',
      name: title,
      gtin13: bibBrief.get('isbns'),
      offers: getOffers(bib, library, libraryLinkedData)
    });

    // Use @graph notation for multiple schemas
    return Immutable.Map({
      '@graph': Immutable.List([creativeWorkSchema, productSchema])
    });
  }
);

export default {
  selectLinkedDataForCurrentBib
};
